import { BaseOrder, Status } from '../../models/orders.model';
import { patchState, signalStore, withHooks, withMethods, withState, getState } from '@ngrx/signals';
import { effect, inject } from '@angular/core';
import { DataFetchService } from '../services/data-fetch/data-fetch.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { withPagination } from './features/pagination.feature';
import { withColumnOrdering } from './features/column-ordering.feature';
import { withFilterStr } from './features/filter-str.feature';
import { withSorting } from './features/sorting.feature';
import { withViewMode } from './features/view-mode.feature';
import { DetailsStore } from './order-details.store';
import { Router } from '@angular/router'; 

type OrdersState = {
  data: BaseOrder[];
  filteredData: BaseOrder[];
};

const initialState: OrdersState = {
  data: [],
  filteredData: [],
};

export const ListStore = signalStore(
  withState(initialState),
  withColumnOrdering(),
  withPagination(),
  withFilterStr(),
  withSorting(),
  withViewMode(),
  withMethods((store) => ({
    updateFilteredDataset(filters: string[], pageReset: boolean = true) {
      const oldState = getState(store) as OrdersState;
      const _filterItems = (items: BaseOrder[], filters: string[]) => {
        return items.filter(item => {
          const itemCopy = { ...item };
          if (itemCopy.orderDate) {
            const formattedDate = new Date(itemCopy.orderDate).toLocaleDateString('en-GB').replaceAll("/", ".");
            itemCopy.orderDate = formattedDate;
          }
          const combinedString = Object.values(itemCopy).join(' ').toLowerCase();
          return filters.every(filter => combinedString.includes(filter.toLowerCase()));
        });
      }
      let filteredItems;
      if (filters.length > 0) {
        filteredItems = _filterItems(oldState.data, filters);
      } else {
        filteredItems = oldState.data;
      }            
      patchState(store, {
        filteredData: [...filteredItems],
        ...(pageReset?{pageIndex:0}:{})
      });
    }
  })),
  withMethods((store, fetchService = inject(DataFetchService), detailsStore = inject(DetailsStore)) => ({
    updateOrderStatus: rxMethod<{ orderId: string, newStatus: Status }>(
      pipe(
        switchMap(({ orderId, newStatus }) =>
          fetchService.updateOrderStatus(orderId, newStatus).pipe(
            tapResponse({
              next: () => {
                const state = getState(store);
                const updatedOrders = state.data.map(order =>
                  order.orderId === orderId ? { ...order, orderStatus: newStatus } : order
                );
                patchState(store, {
                  data: updatedOrders,
                });
                detailsStore.updateLocalOrderStatus(orderId, newStatus);
                store.updateFilteredDataset(state.filterStr, false);
              },
              error: error => console.error(error)
            })
          )
        )
      )
    ),
    fetchAllOrders: rxMethod<void>(
      pipe(
        switchMap(() => {
          return fetchService.getOrders().pipe(
            tapResponse({
              next: orders => {
                patchState(store, { data: orders });
                const state = getState(store);
                if (!state.displayedColumns.length) {
                  store.updateDisplayedColumns(Object.keys(orders[0]));
                }
                store.updateFilteredDataset(state.filterStr, false);
              },
              error: error => console.error(error)
            })
          );
        })
      )
    ),
    updateOrder: rxMethod<BaseOrder>(
      pipe(
        switchMap((order: BaseOrder) => {
          const state = getState(store);
          const updatedOrders = state.data.map(oldOrder =>
            oldOrder.orderId === order.orderId ? order : oldOrder
          );
          patchState(store, {
            data: updatedOrders,
          });
          return of(order); 
        })
      ))
  })),
  withHooks({
    onInit(store) {
      store.fetchAllOrders()

      const router = inject(Router);



      effect(() => {
        const state = getState(store);
        const filters = state.filterStr.join(',');
        const {pageIndex} = state;
        const {selectedViewMode} = state;
        const {sorting} = state           
        const urlTree = router.createUrlTree([], {
          queryParams: {
            filter: filters.length ? filters : null,
            page: pageIndex,
            view: selectedViewMode,
            sortCol: sorting.direction ? sorting.active : null,
            sortDir: sorting.direction ? sorting.direction : null            
          },

        });
        window.history.replaceState(null, '', router.serializeUrl(urlTree));
       
      });




      const urlParams = new URLSearchParams(router.routerState.snapshot.url.split('?')[1]);

      const filter = urlParams.get('filter');
      if (filter) {
        const filters = filter.split(',');
        patchState(store, { filterStr: filters });
        store.updateFilteredDataset(filters);
      }
      const pageIndex = urlParams.get('page');      
      if (pageIndex!==null) {
        patchState(store, { pageIndex: parseInt(pageIndex, 10) });
      }
      

      const selectedViewMode = urlParams.get('view');      
      if (selectedViewMode) {
        patchState(store, { selectedViewMode: selectedViewMode as 'list' | 'cards'});
      }

      const sortCol = urlParams.get('sortCol');
      const sortDir = urlParams.get('sortDir');
      if (sortDir&& sortCol) {
        patchState(store, { sorting: {active:sortCol,direction:sortDir as 'asc' | 'desc' | ''} });
      }

    }
  }),
  // Removed withUpdaters as it is not exported from @ngrx/signals
);
